import { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle2, Heart, Loader2, MapPin } from 'lucide-react';
import { Badge, EmptyState, useToast } from '@qh/shared';
import { useSavedJobs } from '../../context/SavedJobsContext';
import { useAppliedJobs } from '../../context/AppliedJobsContext';
import { useAuth } from '../../context/AuthContext';
import { useProfileGate } from '../../components/profile/ProfileCompletionGate';
import { ApiError, auth, candidates as candidateApply } from '../../lib/apiClient';
import { formatRelativeTime } from '../../lib/relativeTime';
import { forgetInviteToken, readInviteToken } from '../../lib/inviteToken';
import { useDeclarationGate } from '../../components/profile/DeclarationGate';
import type { SavedJobItem, SavedJobStatus } from '../../lib/apiClient';

/**
 * My Saved Jobs (analysis §5.6) — bookmarked jobs the candidate can quickly come
 * back to. Apply triggers the existing apply flow (with profile completion hard
 * gate when needed); Unsave is optimistic via the SavedJobsContext.
 *
 * Cache lives in SavedJobsContext so the JobDetailPage heart and this list share
 * a single source of truth without redundant fetches.
 */
export function MySavedJobsPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { candidate, setCandidate } = useAuth();
  const { savedJobs, unsaveJob } = useSavedJobs();
  const { isJobApplied, markApplied } = useAppliedJobs();
  const { openHard } = useProfileGate();
  const { requireDeclarations } = useDeclarationGate();
  const [pendingUnsave, setPendingUnsave] = useState<string | null>(null);
  const [pendingApply, setPendingApply] = useState<string | null>(null);

  const handleUnsave = useCallback(
    async (jobId: string) => {
      setPendingUnsave(jobId);
      try {
        await unsaveJob(jobId);
        toast.success('Removed from saved jobs.');
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'Could not remove this saved job.');
      } finally {
        setPendingUnsave(null);
      }
    },
    [toast, unsaveJob],
  );

  const handleApply = useCallback(
    async (item: SavedJobItem) => {
      const job = item.job;
      if (!job) return;
      // Region check — apply is region-scoped per Phase 6 candidate rules.
      if (candidate && candidate.region.slug !== job.region.slug) {
        toast.error(`This job is for ${job.region.name}. Your account is in ${candidate.region.name}.`);
        return;
      }
      // Profile gate — incomplete candidate must complete before applying. Closing
      // the modal without saving cancels the apply and shows a toast so the user
      // knows what just happened.
      //
      // Authoritative re-check against /me before deciding (matches the
      // JobDetailPage fix) — AuthContext's candidate is bootstrap-cached and can
      // be stale (cross-tab completion, post-resume-change refresh missed, etc.).
      // Failure mode: fall back to cached candidate; worst case is one extra
      // modal display vs. blocking the user on a transient network error.
      // Auth-method agnostic — applies to Google and email/password candidates
      // identically. Uses the same `pendingApply` per-job loading state that
      // already disables the Apply button below.
      if (candidate) {
        setPendingApply(job.id);
        let fresh = candidate;
        try {
          const me = await auth.me();
          fresh = me;
          setCandidate(me);
        } catch {
          // Fall through with cached candidate.
        }
        if (!fresh.profile_completed) {
          setPendingApply(null);
          const opened = openHard({
            jobTitle: job.title,
            onAfterComplete: () => {
              void submitApply(job.id, job.slug);
            },
            onCancel: () => {
              toast.info('Complete your profile to apply for jobs.');
            },
          });
          if (opened) return;
        }
      }
      void submitApply(job.id, job.slug);

      // `afterDeclaration` marks the retry after the declaration popup (step 5e), so a second
      // DECLARATION_REQUIRED falls through to the normal error instead of reopening it.
      async function submitApply(jobId: string, slug: string, afterDeclaration = false) {
        setPendingApply(jobId);
        try {
          // Recruitment workflow §6.4 — attribute to the candidate's invite when the invite
          // page saved a token for this job; the server ignores a token that isn't theirs.
          const inviteToken = readInviteToken(jobId);
          await candidateApply.apply({ job_id: jobId, ...(inviteToken ? { invite_token: inviteToken } : {}) });
          forgetInviteToken(jobId);
          // Optimistically mark applied so the cross-page provider reflects it
          // instantly (Saved Jobs button flips to "Applied", Jobs listing badge
          // shows) without waiting for the next refresh.
          markApplied(jobId);
          toast.success('Application submitted.');
          // Move the candidate to the application detail.
          navigate(`/dashboard/applications`);
        } catch (err) {
          if (err instanceof ApiError && err.code === 'ALREADY_APPLIED') {
            forgetInviteToken(jobId);
            // Server says they already applied — provider may not have it yet
            // (race / stale cache), so reconcile here too.
            markApplied(jobId);
            toast.success('You already applied to this job.');
            navigate(`/dashboard/applications`);
            return;
          }
          // Recruitment workflow §6.4 (step 5e) — the fraud declaration is missing: collect it
          // in the popup and retry once, staying on Saved Jobs.
          if (err instanceof ApiError && err.code === 'DECLARATION_REQUIRED' && !afterDeclaration) {
            requireDeclarations({
              jobTitle: job!.title,
              onDone: () => {
                void submitApply(jobId, slug, true);
              },
              onCancel: () => {
                toast.info('Agree to the fraud declaration to apply.');
              },
            });
            return;
          }
          if (err instanceof ApiError) {
            toast.error(err.message);
          } else {
            toast.error('Could not submit your application.');
          }
          // Fall back to the job page so the candidate can retry / see why.
          navigate(`/job-seekers/${slug}`);
        } finally {
          setPendingApply(null);
        }
      }
    },
    [candidate, navigate, openHard, requireDeclarations, setCandidate, toast],
  );

  return (
    <section>
      <header className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-brand-primary">
          Saved Jobs
        </h1>
        <p className="text-sm text-brand-muted mt-1">
          Roles you've bookmarked from the jobs feed. Apply or remove anytime.
        </p>
      </header>

      {savedJobs === null ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
        </div>
      ) : savedJobs.length === 0 ? (
        <EmptyState
          title="No saved jobs yet"
          description="Bookmark jobs from the jobs listing to come back to them later."
          icon={<Heart className="w-8 h-8 text-brand-muted" />}
          action={
            <Link
              to="/job-seekers"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-brand-deep transition-all"
            >
              Browse jobs
            </Link>
          }
        />
      ) : (
        <ul className="space-y-3">
          {savedJobs.map((item) => (
            <SavedJobCard
              key={item.job_id}
              item={item}
              onUnsave={handleUnsave}
              onApply={handleApply}
              unsaving={pendingUnsave === item.job_id}
              applying={pendingApply === item.job_id}
              alreadyApplied={item.job ? isJobApplied(item.job.id) : false}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function SavedJobCard({
  item,
  onUnsave,
  onApply,
  unsaving,
  applying,
  alreadyApplied,
}: {
  item: SavedJobItem;
  onUnsave: (jobId: string) => void;
  onApply: (item: SavedJobItem) => void;
  unsaving: boolean;
  applying: boolean;
  alreadyApplied: boolean;
}) {
  const job = item.job;
  if (!job) {
    // Shouldn't happen — the backend always includes the job snapshot. Guard anyway.
    return (
      <li className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5">
        <p className="text-sm text-brand-muted">This saved job is no longer available.</p>
        <button
          type="button"
          onClick={() => onUnsave(item.job_id)}
          disabled={unsaving}
          className="text-xs font-bold text-red-700 hover:text-red-900 mt-2"
        >
          Remove
        </button>
      </li>
    );
  }
  const status = job.status;
  const canApply = status === 'PUBLISHED';
  const availabilityNote = unavailableNote(status);

  return (
    <li className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-5 hover:border-brand-accent/40 transition-colors">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Link
            to={`/job-seekers/${job.slug}`}
            className="text-base sm:text-lg font-display font-bold text-brand-primary hover:text-brand-deep transition-colors block truncate"
          >
            {job.title}
          </Link>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 text-brand-muted">
              <MapPin className="w-3 h-3" />
              {job.region.name}
              {job.cities.length > 0 ? ` · ${job.cities.map((c) => c.name).join(', ')}` : ''}
            </span>
            <span className="text-brand-muted">·</span>
            <span className="text-brand-muted">Saved {formatRelativeTime(item.saved_at)}</span>
            {availabilityNote && (
              <Badge tone={availabilityNote.tone}>{availabilityNote.label}</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {canApply &&
            (alreadyApplied ? (
              // Matches JobDetailPage's "Applied" treatment (emerald-50 /
              // emerald-200 / emerald-800 + CheckCircle2). Non-interactive: the
              // candidate views the job via the title link above; no separate
              // affordance is needed.
              <span
                aria-disabled="true"
                className="px-4 py-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl font-black text-xs uppercase tracking-widest inline-flex items-center gap-1.5 cursor-default"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Applied
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onApply(item)}
                disabled={applying}
                className="px-4 py-2 bg-brand-primary text-white rounded-xl font-black text-xs uppercase tracking-widest hover:bg-brand-deep transition-all disabled:opacity-60 inline-flex items-center gap-1.5"
              >
                {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                Apply
              </button>
            ))}
          <button
            type="button"
            onClick={() => onUnsave(item.job_id)}
            disabled={unsaving}
            className="px-4 py-2 border border-gray-200 text-brand-primary rounded-xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all disabled:opacity-60"
          >
            {unsaving ? 'Removing…' : 'Unsave'}
          </button>
        </div>
      </div>
    </li>
  );
}

function unavailableNote(
  status: SavedJobStatus,
): { label: string; tone: 'gray' | 'amber' | 'red' } | null {
  switch (status) {
    case 'PUBLISHED':
      return null;
    case 'UNPUBLISHED':
    case 'ON_HOLD':
      return { label: 'Temporarily unavailable', tone: 'amber' };
    case 'CLOSED':
    case 'DELETED':
    case 'UNAVAILABLE': // step 5d — the job's company is no longer listed; the website hides the job
      return { label: 'No longer available', tone: 'red' };
    default:
      return { label: 'Not available', tone: 'gray' };
  }
}
